import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Users, Wallet, Loader2, BadgeCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { getSession, updateSelections, updatePayment } from "@/lib/api";
import { getGuest } from "@/lib/store";
import { fmt, lineTotal, computeLocal, buildRevolutUrl, itemUnits } from "@/lib/calc";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose,
} from "@/components/ui/drawer";

const METHODS = [
  { id: "cash", label: "Cash" },
  { id: "revolut", label: "Revolut" },
  { id: "card", label: "Card" },
];

export default function SelectItems() {
  const { code } = useParams();
  const navigate = useNavigate();
  const me = getGuest(code);
  const [session, setSession] = useState(null);
  const [sel, setSel] = useState({}); // item_id -> [unitIdx]
  const [payOpen, setPayOpen] = useState(false);
  const [method, setMethod] = useState(null);
  const [paying, setPaying] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!me?.guestId) {
      navigate(`/join/${code}`, { replace: true });
      return;
    }
  }, [me, code, navigate]);

  const load = useCallback(() => {
    getSession(code).then((s) => {
      setSession(s);
      const mine = s.guests.find((g) => g.id === me?.guestId);
      if (mine) {
        setSel((prev) =>
          Object.keys(prev).length
            ? prev
            : Object.fromEntries((mine.selections || []).map((x) => [x.item_id, x.units || []]))
        );
        if (mine.paid) setMethod(mine.payment_method);
      }
    }).catch(() => {});
  }, [code, me?.guestId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const persist = useCallback((next) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const selections = Object.entries(next)
        .filter(([, units]) => units && units.length)
        .map(([item_id, units]) => ({ item_id, units }));
      updateSelections(code, me.guestId, selections).then(setSession).catch(() => {});
    }, 350);
  }, [code, me?.guestId]);

  const toggleUnit = (item, unitIdx) => {
    setSel((prev) => {
      const cur = prev[item.id] || [];
      const has = cur.includes(unitIdx);
      const units = has ? cur.filter((u) => u !== unitIdx) : [...cur, unitIdx].sort((a, b) => a - b);
      const next = { ...prev };
      if (units.length) next[item.id] = units;
      else delete next[item.id];
      persist(next);
      return next;
    });
  };

  // merge my local selections into session for live compute
  const liveSession = useMemo(() => {
    if (!session) return null;
    const guests = session.guests.map((g) =>
      g.id === me?.guestId
        ? { ...g, selections: Object.entries(sel).map(([item_id, units]) => ({ item_id, units })) }
        : g
    );
    return { ...session, guests };
  }, [session, sel, me?.guestId]);

  const myTotals = liveSession ? computeLocal(liveSession)[me?.guestId] : null;
  const mineGuest = session?.guests.find((g) => g.id === me?.guestId);

  // guests (names) claiming a specific unit of an item, from live session
  const unitClaimers = (itemId, unitIdx) =>
    (liveSession?.guests || [])
      .filter((g) => (g.selections || []).some((s) => s.item_id === itemId && (s.units || []).includes(unitIdx)))
      .map((g) => g.name);

  const handlePay = async () => {
    if (!method) return toast.error("Alege metoda de plată");
    setPaying(true);
    try {
      const s = await updatePayment(code, me.guestId, true, method);
      setSession(s);
      setPayOpen(false);
      toast.success("Marcat ca plătit");
    } catch {
      toast.error("Eroare");
    } finally {
      setPaying(false);
    }
  };

  if (!session) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  const currency = session.currency;
  const isPaid = mineGuest?.paid;

  return (
    <div className="min-h-screen bg-background pb-40">
      <Header subtitle={session.restaurant_name || "Alege produsele"} />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-8">
        <h1 className="font-display text-3xl font-black tracking-tight">Salut, {me?.name}!</h1>
        <p className="text-muted-foreground mt-2">Bifează ce ai consumat. Pentru produsele cu mai multe porții, alege exact porția ta — cei care aleg aceeași porție o împart automat.</p>

        <div className="mt-6 space-y-3">
          {session.items.map((item, i) => {
            const { numUnits, unitValue } = itemUnits(item);
            const mySel = sel[item.id] || [];
            const active = mySel.length > 0;

            // Single-portion item: whole card is one toggle
            if (numUnits <= 1) {
              const claimers = unitClaimers(item.id, 0);
              const shared = claimers.length > 1;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={active}
                  data-testid={`select-item-${i}`}
                  onClick={() => !isPaid && toggleUnit(item, 0)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !isPaid && toggleUnit(item, 0)}
                  className={`w-full text-left rounded-[20px] p-4 border transition-all cursor-pointer ${
                    active ? "border-interactive bg-interactive/5 ring-1 ring-interactive" : "border-border bg-card"
                  } ${isPaid ? "opacity-60 pointer-events-none" : "active:scale-[0.99]"}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 shrink-0 rounded-md border-2 flex items-center justify-center transition ${active ? "bg-interactive border-interactive text-white" : "border-border"}`}>
                      {active && <Check size={14} />}
                    </span>
                    <div className="flex-1">
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {fmt(item.unit_price, currency)}
                        {shared && active && <span className="text-interactive font-medium"> · împărțit la {claimers.length}</span>}
                      </p>
                    </div>
                    <span className="font-mono font-bold">{fmt(lineTotal(item), currency)}</span>
                  </div>
                </motion.div>
              );
            }

            // Multi-portion item: one chip per portion
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                data-testid={`select-item-${i}`}
                className={`w-full rounded-[20px] p-4 border transition-all ${
                  active ? "border-interactive bg-interactive/5 ring-1 ring-interactive" : "border-border bg-card"
                } ${isPaid ? "opacity-60 pointer-events-none" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {numUnits} porții × {fmt(unitValue, currency)}
                    </p>
                  </div>
                  <span className="font-mono font-bold">{fmt(lineTotal(item), currency)}</span>
                </div>

                <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-muted-foreground mt-3 mb-2">
                  Ce porție ai luat?
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {Array.from({ length: numUnits }).map((_, u) => {
                    const claimers = unitClaimers(item.id, u);
                    const mineOn = mySel.includes(u);
                    const others = claimers.filter((n) => n !== me?.name);
                    const splitBy = claimers.length;
                    return (
                      <button
                        key={u}
                        data-testid={`portion-${i}-${u}`}
                        disabled={isPaid}
                        onClick={() => toggleUnit(item, u)}
                        className={`text-left rounded-xl p-3 border transition active:scale-[0.98] ${
                          mineOn ? "border-interactive bg-interactive/10" : "border-border bg-secondary"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold">Porția {u + 1}</span>
                          <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${mineOn ? "bg-interactive border-interactive text-white" : "border-border"}`}>
                            {mineOn && <Check size={12} />}
                          </span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                          {splitBy === 0 && <span className="text-[11px] text-muted-foreground">liberă</span>}
                          {splitBy > 0 && (
                            <span className={`text-[11px] font-medium ${splitBy > 1 ? "text-interactive" : "text-muted-foreground"}`}>
                              {splitBy > 1 ? `împărțit la ${splitBy} · ` : ""}
                              {fmt(unitValue / splitBy, currency)}
                            </span>
                          )}
                        </div>
                        {others.length > 0 && (
                          <p className="text-[10px] text-muted-foreground mt-0.5 truncate flex items-center gap-1">
                            <UserRound size={9} /> cu {others.join(", ")}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-6 bg-card border border-border rounded-[24px] p-5 flex items-center gap-2 text-sm text-muted-foreground">
          <Users size={16} />
          {session.guests.length} persoane în această sesiune
        </div>
      </main>

      {/* Sticky total bar */}
      <div className="fixed bottom-0 left-0 right-0 z-[60] glass bg-background/85 border-t border-border p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Totalul tău</span>
            <p data-testid="your-total" className="font-display text-3xl font-black tracking-tight">{fmt(myTotals?.total || 0, currency)}</p>
          </div>
          {isPaid ? (
            <div data-testid="paid-badge" className="flex items-center gap-2 bg-success/10 text-success rounded-full px-5 py-3 font-bold">
              <BadgeCheck size={20} /> Plătit
            </div>
          ) : (
            <button
              data-testid="open-pay-button"
              onClick={() => setPayOpen(true)}
              className="bg-primary text-primary-foreground rounded-full py-4 px-7 text-lg font-bold active:scale-[0.98] transition-transform flex items-center gap-2 relative z-[61]"
            >
              <Wallet size={20} /> Plătesc
            </button>
          )}
        </div>
      </div>

      <Drawer open={payOpen} onOpenChange={setPayOpen}>
        <DrawerContent>
          <div className="max-w-md mx-auto w-full px-4 pb-8">
            <DrawerHeader className="px-0">
              <DrawerTitle className="font-display text-2xl font-black">Cum plătești {fmt(myTotals?.total || 0, currency)}?</DrawerTitle>
            </DrawerHeader>
            <p className="text-sm text-muted-foreground -mt-2 mb-4">Către {session.host_name}. Notează metoda aleasă.</p>
            <div className="grid grid-cols-3 gap-3">
              {METHODS.map((m) => (
                <button
                  key={m.id}
                  data-testid={`method-${m.id}`}
                  onClick={() => setMethod(m.id)}
                  className={`rounded-2xl py-4 font-semibold border transition active:scale-[0.97] ${
                    method === m.id ? "border-interactive bg-interactive/10 text-interactive" : "border-border bg-card"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {method === "revolut" && session.revolut_link && (
              <a
                data-testid="open-revolut-button"
                href={buildRevolutUrl(session.revolut_link, myTotals?.total || 0, currency)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 w-full bg-[#0066FF] text-white rounded-full py-4 text-lg font-bold active:scale-[0.98] transition flex justify-center items-center gap-2"
              >
                Deschide Revolut · {fmt(myTotals?.total || 0, currency)}
              </a>
            )}
            {method === "revolut" && !session.revolut_link && (
              <p className="mt-4 text-sm text-muted-foreground text-center">
                {session.host_name} nu a adăugat un link Revolut. Trimite-i manual.
              </p>
            )}
            <button
              data-testid="confirm-pay-button"
              onClick={handlePay}
              disabled={paying}
              className="mt-5 w-full bg-primary text-primary-foreground rounded-full py-4 text-lg font-bold active:scale-[0.98] transition flex justify-center items-center gap-2 disabled:opacity-60"
            >
              {paying ? <Loader2 className="animate-spin" size={20} /> : "Marchează ca plătit"}
            </button>
            <DrawerClose asChild>
              <button className="mt-3 w-full py-3 text-muted-foreground font-medium" data-testid="cancel-pay-button">Anulează</button>
            </DrawerClose>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
